import { toast } from 'react-toastify';
import { all, call, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { ApiResponse } from 'api/ApiClient';
import { SuccessToastContainer } from 'components/Toast/ToastContainer';
import { ConnectIntegrationType } from 'hooks/useConnectIntegration';
import { AuthService, CurrentEmployeeService, InviteService, SignUpService } from 'lib';
import { routeConstants } from 'routes';
import { browserHistory } from 'routes/Router';
import { selectInvite } from 'store/Invites';
import * as InviteActions from 'store/Invites/actions';
import { ICurrentEmployee } from 'store/types';
import {
  ChannelType,
  IAction,
  IAuthMFARequired,
  IAuthRequest,
  IAuthResendVerificationCode,
  IAuthToken,
  ICardSession,
  IForgotPasswordRequest,
  IForgotPasswordVerifyRequest,
  IForgotPasswordVerifyToken,
  IGoogleUser,
  IGoogleUserRequest,
  IIdentity,
  IInvite,
  IResetPasswordRequest,
} from 'types';
import { formatErrorMessages } from 'utils/errors';
import * as API from '../../api';
import * as Actions from './actions';
import * as ActionTypes from './types';

export function* setCurrentEmployee({ payload }: IAction<ActionTypes.App, { body: ICurrentEmployee }>) {
  try {
    if (payload?.body) yield call(CurrentEmployeeService.set, payload.body);
    if (!payload?.body) yield call(CurrentEmployeeService.clear);
    yield put(Actions.setCurrentEmployeeSuccess(payload?.body));
  } catch (error: any) {
    yield put(Actions.setCurrentEmployeeFail(error));
  }
}

export function* login({ payload }: IAction<ActionTypes.App, { body: IAuthRequest; onMFARequired(request: IAuthMFARequired): void; onFail?(error: any): void; integration?: ConnectIntegrationType }>) {
  const { body, onMFARequired, onFail, integration } = payload;
  try {
    const response: ApiResponse<IAuthToken | IAuthMFARequired> = yield call(API.Authentication.login, body);
    // Handle successful login
    if (response.statusCode === 201) {
      yield put(Actions.loginSuccess(response.body as IAuthToken));
      yield call(loginInviteRedirect, integration);
    }

    // Handle MFA Required login.
    // In the case where the API returns a 206, MFA is required for the user to complete authenication
    if (response.statusCode === 206) {
      yield call(onMFARequired, response.body as IAuthMFARequired);
      yield put(Actions.loginMFARequired(response.body as IAuthMFARequired));
    }
  } catch (error: any) {
    if (onFail) yield call(onFail, error);
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode });
    yield put(Actions.loginFail(formattedError, error.statusCode >= 500));
  }
}

export function* loginInviteRedirect(integration?: ConnectIntegrationType) {
  const invite: Partial<IInvite> = yield select(selectInvite);
  if (invite?.id) {
    yield put(InviteActions.getInviteRequest(invite.id, () => browserHistory.replace(routeConstants.private.acceptInvitation)));
  } else if (integration) {
    yield call(browserHistory.push, `/connect/${integration}/connections`);
  } else {
    yield call(browserHistory.push, routeConstants.private.rootPath); // Redirect to application root
  }
}

export function* logOut({ payload }: IAction<ActionTypes.App, { redirectUrl?: string }>) {
  try {
    const { redirectUrl } = payload;
    yield call(AuthService.clearToken); // Remove the users auth token
    yield call(browserHistory.push, redirectUrl || routeConstants.public.login); // Redirect to redirectUrl or default to login
    yield call(CurrentEmployeeService.clear); // Removes reference to selected employee
    yield call(SignUpService.clearUser); // Remove sign-up user reference
    yield call(InviteService.clearInviteToken); // Remove invite token references
    yield call(InviteService.clearInviteIgnored); // Remove ignored invite token references
    yield put(Actions.logOutSuccess());
  } catch (error: any) {
    yield put(Actions.logOutFail(error));
  }
}

export function* forgotPassword({ payload }: IAction<ActionTypes.App, { body: IForgotPasswordRequest; onSuccess?(): void; toastMessage?: string }>) {
  try {
    const { body, onSuccess, toastMessage } = payload;
    const response: ApiResponse<IIdentity> = yield call(API.Authentication.forgotPassword, body);

    if (onSuccess) yield call(onSuccess);
    yield put(Actions.forgotPasswordSuccess(response.body));
    if (toastMessage) {
      yield call(toast, SuccessToastContainer({ message: toastMessage }));
    }
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode });
    yield put(Actions.forgotPasswordFail(formattedError, error.statusCode >= 500));
  }
}

export function* forgotPasswordVerify({ payload }: IAction<ActionTypes.App, { identityId: string; body: IForgotPasswordVerifyRequest; redirectUrl?: string }>) {
  try {
    const { identityId, body, redirectUrl } = payload;
    const response: ApiResponse<IForgotPasswordVerifyToken> = yield call(API.Authentication.forgotPasswordVerify, identityId, body);

    yield call(toast, SuccessToastContainer({ message: 'Successfully verified code' }));
    yield put(Actions.forgotPasswordVerifySuccess(response.body));
    if (redirectUrl) yield call(browserHistory.push, redirectUrl);
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode });
    yield put(Actions.forgotPasswordVerifyFail(formattedError, error.statusCode >= 500));
  }
}

export function* resetPassword({ payload }: IAction<ActionTypes.App, { identityId: string; body: IResetPasswordRequest; toastMessage?: string }>) {
  try {
    const { identityId, body, toastMessage } = payload;
    const response: ApiResponse<any> = yield call(API.Authentication.resetPassword, identityId, body);

    yield put(Actions.resetPasswordSuccess(response.body));
    if (toastMessage) {
      yield call(toast, SuccessToastContainer({ message: toastMessage }));
    }
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode });
    yield put(Actions.resetPasswordFail(formattedError, error.statusCode >= 500));
  }
}

export function* mfaVerify({ payload }: IAction<ActionTypes.App, { authId: string; code: string; onSuccess?(): void }>) {
  try {
    const { authId, code, onSuccess } = payload;
    const response: ApiResponse<IAuthToken> = yield call(API.Authentication.loginMFAVerify, authId, code);

    yield put(Actions.mfaVerifySuccess(response.body));
    if (onSuccess) {
      yield call(onSuccess);
    } else {
      yield call(loginInviteRedirect);
    }
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode });
    yield put(Actions.mfaVerifyFail(formattedError, error.statusCode >= 500));
  }
}

export function* mfaResend({ payload }: IAction<ActionTypes.App, { authId: string; channel?: ChannelType; onSuccess?(): void }>) {
  try {
    const { authId, channel, onSuccess } = payload;
    const response: ApiResponse<IAuthResendVerificationCode> = yield call(API.Authentication.resendCodeMFA, authId, channel);

    if (onSuccess) yield call(onSuccess);
    yield call(toast, SuccessToastContainer({ message: channel && channel === ChannelType.CALL ? 'Verification call initiated' : 'Verification code resent' }));
    yield put(Actions.mfaResendSuccess(response.body));
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode });
    yield put(Actions.mfaResendFail(formattedError, error.statusCode >= 500));
  }
}

export function* getGoogleUser({ payload }: IAction<ActionTypes.App, { body: IGoogleUserRequest; onSuccess(googleUser: IGoogleUser): void }>) {
  try {
    const { body, onSuccess } = payload;
    const response: ApiResponse<IGoogleUser> = yield call(API.Authentication.getGoogleUser, body);

    yield call(onSuccess, response.body);
    yield put(Actions.getGoogleUserSuccess(response.body));
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode });
    yield put(Actions.getGoogleUserFail(formattedError));
  }
}

export function* getCardSession({ payload }: IAction<ActionTypes.App, { hostUri: string }>) {
  try {
    const { hostUri } = payload;
    const response: ApiResponse<ICardSession> = yield call(API.Cards.getCardSession, hostUri);

    yield put(Actions.getCardSessionSuccess(response.body));
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode });
    yield put(Actions.getCardSessionFail(formattedError, error.statusCode >= 500));
  }
}

export function* watchSetCurrentEmployee() {
  yield takeLatest(ActionTypes.App.SET_CURRENT_EMPLOYEE_REQUEST, setCurrentEmployee);
}

export function* watchLogin() {
  yield takeEvery(ActionTypes.App.LOGIN_REQUEST, login);
}

export function* watchLogOut() {
  yield takeEvery(ActionTypes.App.LOGOUT_REQUEST, logOut);
}

export function* watchForgotPassword() {
  yield takeEvery(ActionTypes.App.FORGOT_PASSWORD_REQUEST, forgotPassword);
}

export function* watchForgotPasswordVerify() {
  yield takeEvery(ActionTypes.App.FORGOT_PASSWORD_VERIFY_REQUEST, forgotPasswordVerify);
}

export function* watchResetPassword() {
  yield takeEvery(ActionTypes.App.RESET_PASSWORD_REQUEST, resetPassword);
}

export function* watchMfaVerify() {
  yield takeEvery(ActionTypes.App.MFA_VERIFY_REQUEST, mfaVerify);
}

export function* watchMfaResend() {
  yield takeEvery(ActionTypes.App.MFA_RESEND_REQUEST, mfaResend);
}

export function* watchGetGoogleUser() {
  yield takeEvery(ActionTypes.App.GET_GOOGLE_USER_REQUEST, getGoogleUser);
}

export function* watchGetCardSession() {
  yield takeEvery(ActionTypes.App.GET_CARD_SESSION_REQUEST, getCardSession);
}

function* appSaga() {
  yield all([
    fork(watchSetCurrentEmployee),
    fork(watchLogin),
    fork(watchLogOut),
    fork(watchForgotPassword),
    fork(watchForgotPasswordVerify),
    fork(watchResetPassword),
    fork(watchMfaVerify),
    fork(watchMfaResend),
    fork(watchGetGoogleUser),
    fork(watchGetCardSession),
  ]);
}
export { appSaga };
